import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  Segment, Loader, Form, Button, Message,
} from 'semantic-ui-react';
import moment from 'moment';
import DataContext from '../../contexts/DataContext';
import renderChatText from './formatChatText';

// Mirrors MAX_CHAT_MESSAGE_LENGTH in api/src/ai/googleVertexAI/chatService.ts,
// which is the value actually enforced server-side — keep the two in sync.
const MAX_CHAT_MESSAGE_LENGTH = 4000;

// Only ever shown on a user's own message — the assistant's reply is
// rendered as just the message, with no "AI" label.
const senderName = (message, currentUser) => {
  if (!message.sentBy) return 'Unknown';
  if (currentUser && message.sentBy.id === currentUser.id) return 'You';
  return [message.sentBy.firstName, message.sentBy.lastName].filter(Boolean).join(' ') || message.sentBy.email;
};

const formatTimestamp = (createdAt) => (createdAt ? moment(createdAt).calendar(null, {
  sameElse: 'MMM D, YYYY [at] LT',
}) : '');

const assistantMessageBody = (message, onReferenceClick) => {
  if (message.status === 'failed') {
    const detail = message.error ? ` (${message.error})` : '';
    return `Sorry, something went wrong generating a reply${detail}.`;
  }
  return renderChatText(message.text, onReferenceClick);
};

// `header` renders above the thread (scope label, Share button). `readOnly`
// replaces the input with `readOnlyNote`, for a session shared with the user.
const ChatWindow = ({
  messages, loading, sending, error, onSend, onSelectReference,
  header, readOnly, readOnlyNote, placeholder, emptyText,
}) => {
  const { user } = useContext(DataContext);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    onSend(text);
  };

  // Reference ids ("R1", "R2", ...) are only unique within the message that
  // produced them — each assistant reply renumbers from R1 — so a reference
  // must always be looked up within the specific message that was clicked,
  // never across the whole session's messages.
  const handleReferenceClick = (message, refId) => {
    const reference = (message.references || []).find((r) => r.id === refId);
    if (reference) onSelectReference(reference);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {header}
      <div style={{
        flex: 1, overflowY: 'auto', minHeight: '50vh', paddingRight: '0.5em',
      }}
      >
        {loading && <Loader active inline="centered" content="Loading messages..." />}
        {!loading && messages.length === 0 && (
          <Segment basic textAlign="center">
            <p>{emptyText || 'Ask a question about this project to get started.'}</p>
          </Segment>
        )}
        {messages.map((message) => {
          const isUser = message.role === 'user';
          const isFailed = message.status === 'failed';
          return (
            <div
              key={message.id}
              style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: '0.5em' }}
            >
              <Segment
                compact
                style={{ maxWidth: '80%', backgroundColor: isUser ? '#eef4fc' : undefined }}
                color={isUser ? undefined : 'blue'}
                negative={isFailed}
              >
                {isUser && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1em' }}>
                    <strong>{senderName(message, user)}</strong>
                    <span style={{ fontSize: '0.75em', color: '#999' }}>
                      {formatTimestamp(message.createdAt)}
                    </span>
                  </div>
                )}
                <div style={{ whiteSpace: isUser ? 'pre-wrap' : 'normal', marginTop: isUser ? '0.25em' : 0 }}>
                  {isUser
                    ? message.text
                    : assistantMessageBody(
                      message,
                      (refId) => handleReferenceClick(message, refId),
                    )}
                </div>
              </Segment>
            </div>
          );
        })}
        {sending && <Loader active inline="centered" content="Thinking..." />}
        <div ref={bottomRef} />
      </div>
      {error && (
        <Message negative>
          <p>{error}</p>
        </Message>
      )}
      {readOnly ? (
        <Message info size="small" content={readOnlyNote} />
      ) : (
        <Form onSubmit={handleSubmit} style={{ marginTop: '0.5em' }}>
          <Form.TextArea
            value={draft}
            onChange={(e, { value }) => setDraft(value.slice(0, MAX_CHAT_MESSAGE_LENGTH))}
            placeholder={placeholder || 'Ask a question about this project...'}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
          >
            <span style={{ fontSize: '0.85em', color: '#999' }}>
              {`${draft.length} / ${MAX_CHAT_MESSAGE_LENGTH} characters`}
            </span>
            <Button primary type="submit" disabled={!draft.trim() || sending} loading={sending}>
              Send
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default ChatWindow;
